/**
 * App-side wiring a registry-served settings screen needs in place before it
 * renders, keyed by CONTRIBUTION ID and owned by composition.
 *
 * A screen that lives in a workspace package cannot import the app's settings
 * chrome, its modals or its mutation delegates, and — rendered by the registry
 * as a bare `<Screen />` — it has no mount point to receive them as props
 * either. So it reads them from a `configure…` seam, and the app registers that
 * seam here. This is the third of design D3's injection channels, and a
 * contributed screen is exactly the case where the second one (props at the
 * mount point) is structurally unavailable.
 *
 * **This file is the pattern, and each part of its shape is load-bearing.**
 *
 * - **Composition owns it**, not the settings modal. The registry is
 *   composition's mechanism; a route-keyed table inside the settings loader
 *   would make the settings surface the owner of every domain's wiring, and the
 *   second contended surface to be served from a package would have to invent
 *   this again.
 * - **Keyed by contribution id, not by route.** The id is what a package
 *   declares; the route is how one surface happens to address it. Keying by id
 *   is what lets the same registry serve a surface with no routes at all.
 * - **The values are thunks over a dynamic `import()`.** The wiring modules name
 *   app chrome and the package door carrying the screen, so a static import here
 *   would put the whole screen graph in the first-load JS of every page —
 *   measured at +94,291 bytes gzip on `/_app` when the coding registration was
 *   eager, against a ±0.5% bundle-neutrality allowance. Nothing in this map is
 *   evaluated until the screen it belongs to is loaded.
 * - **A contribution with no entry is legal.** Most screens need no host; the
 *   loader treats a missing entry as "nothing to wire" rather than as an error.
 */
import { type SettingsScreenSlug } from '@/app-shell/settings/atoms/settingsModalSlugs';
import { type SettingsScreenContributionId } from '@heidi/runtime/shell';

/**
 * What a host may be keyed by: a settings-screen contribution id that is also a
 * real settings slug.
 *
 * The shell types a contribution id as a string — the slug vocabulary is the
 * app's IA and a platform package cannot enumerate it — so the narrowing
 * happens here, where the vocabulary exists. A typo'd id fails to compile
 * instead of registering a host nothing ever calls, which is the failure mode
 * worth catching: it fails closed and invisibly, at the first user to open that
 * section.
 *
 * `Extract` rather than an intersection, and the difference is not cosmetic: it
 * keeps the door's id type load-bearing. If `SettingsScreenContributionId` is
 * ever narrowed, a slug that stops being a legal id drops out of this union and
 * its entry stops compiling — where an intersection over a `string` id is
 * flagged as redundant today and would have to be deleted to satisfy lint.
 */
export type SettingsScreenHostId = Extract<
  SettingsScreenSlug,
  SettingsScreenContributionId
>;

export type SettingsScreenHostMap = Partial<
  Record<SettingsScreenHostId, () => Promise<void>>
>;

export const configureWorkspaceSettingsHost = () =>
  import('./hosts/workspace/settings/workspace-settings-host').then((module) =>
    module.configureWorkspaceSettingsUiHost()
  );

export const configureWorkspaceGeneralSettingsHost = () =>
  import('./hosts/workspace/settings/workspace-general-settings-host').then((module) =>
    module.configureWorkspaceGeneralSettingsUiHost()
  );

export const configureGrowthSettingsHost = () =>
  import('./hosts/growth/settings/growth-settings-host').then((module) =>
    module.configureGrowthSettingsUiHost()
  );

export const configureEvidenceSettingsHost = () =>
  import('./hosts/evidence/settings/evidence-settings-host').then((module) =>
    module.configureEvidenceSettingsUiHost()
  );

/**
 * One thunk per contribution id — including the five, and now the two, that
 * share a host.
 *
 * The Comms screens are five contributions over ONE `configure…` call: the seam
 * holds a single host and each screen reads the members it needs, so a per-id
 * host would be five copies of one registration. They still get five entries
 * rather than one, because the map is keyed by contribution id and the rollback
 * walk below reaches a route through its id — collapsing them would leave four
 * of the five routes unhosted the moment their descriptors were withdrawn,
 * which is precisely the state the rollback exists for.
 *
 * `notifications`, `memory` and `general` all read the one `@heidi/workspace`
 * settings host; listing only some would strand the rest on rollback.
 */
export const settingsScreenHosts: SettingsScreenHostMap = {
  coding: () =>
    import('./hosts/coding/settings/coding-settings-host').then((module) =>
      module.configureCodingSettingsUiHost()
    ),
  evidence: () => configureEvidenceSettingsHost(),
  comms: () =>
    import('./hosts/calls/settings/calls-settings-host').then((module) =>
      module.configureCallsSettingsUiHost()
    ),
  'comms-data-management': () =>
    Promise.all([
      import('./hosts/calls/settings/calls-settings-host').then((module) =>
        module.configureCallsSettingsUiHost()
      ),
      configureWorkspaceSettingsHost(),
    ]).then(() => undefined),
  summaries: () =>
    import('./hosts/calls/settings/calls-settings-host').then((module) =>
      module.configureCallsSettingsUiHost()
    ),
  'blocked-numbers': () =>
    import('./hosts/calls/settings/calls-settings-host').then((module) =>
      module.configureCallsSettingsUiHost()
    ),
  'comms-integrations': () =>
    import('./hosts/calls/settings/calls-settings-host').then((module) =>
      module.configureCallsSettingsUiHost()
    ),
  prechart: () =>
    import('./hosts/patients/settings/patients-settings-host').then((module) =>
      module.configurePatientsSettingsUiHost()
    ),
  dictate: () =>
    import('./hosts/dictation/settings/dictate-settings-host').then((module) =>
      module.configureDictateSettingsUiHost()
    ),
  // `defaults` and `data-management` also render sections from
  // `@heidi/session`, so each configures the session UI host as well.
  defaults: () =>
    Promise.all([
      import('./hosts/dictation/settings/dictate-settings-host').then((module) =>
        module.configureDictateSettingsUiHost()
      ),
      import('./hosts/session/settings/session-settings-host').then((module) =>
        module.configureSessionSettingsUiHost()
      ),
      configureGrowthSettingsHost(),
    ]).then(() => undefined),
  notifications: () => configureWorkspaceSettingsHost(),
  memory: () => configureWorkspaceSettingsHost(),
  // The general-only seam loads here so other workspace routes never pay for
  // the upload flow and crop modal.
  general: () =>
    Promise.all([
      configureWorkspaceSettingsHost(),
      configureWorkspaceGeneralSettingsHost(),
    ]).then(() => undefined),
  // `account` declares no contribution and still needs both hosts: its
  // app-shell composer renders Workspace and Evidence settings panels.
  account: () =>
    Promise.all([configureWorkspaceSettingsHost(), configureEvidenceSettingsHost()]).then(
      () => undefined
    ),
  // The team composer mounts Workspace, Growth and Evidence settings sections.
  team: () =>
    Promise.all([
      configureWorkspaceSettingsHost(),
      configureGrowthSettingsHost(),
      configureEvidenceSettingsHost(),
    ]).then(() => undefined),
  organization: () =>
    Promise.all([configureWorkspaceSettingsHost(), configureEvidenceSettingsHost()]).then(
      () => undefined
    ),
  'data-management': () =>
    Promise.all([
      configureWorkspaceSettingsHost(),
      import('./hosts/session/settings/session-settings-host').then((module) =>
        module.configureSessionSettingsUiHost()
      ),
      import('./hosts/patients/settings/patients-settings-host').then((module) =>
        module.configurePatientsSettingsUiHost()
      ),
    ]).then(() => undefined),
  'team-evidence': () =>
    Promise.all([configureWorkspaceSettingsHost(), configureEvidenceSettingsHost()]).then(
      () => undefined
    ),
  // Both are contributed by `@heidi/integrations/contributions` and share one
  // host module.
  inbox: () =>
    import('./hosts/integrations/settings/integrations-settings-host').then((module) =>
      module.configureInboxSettingsUiHost()
    ),
  'integration-agent': () =>
    import('./hosts/integrations/settings/integrations-settings-host').then((module) =>
      module.configureIntegrationAgentSettingsUiHost()
    ),
  billing: () =>
    import('./hosts/commerce/settings/commerce-settings-host').then((module) =>
      module.configureCommerceSettingsUiHost()
    ),
};

/**
 * The host for a contribution id, if it has one.
 *
 * The cast is confined here: a descriptor's id arrives as a plain string from
 * the registry (discovery is mechanical and cannot narrow), while the map's
 * DECLARATION is checked against the slug union above — which is the half that
 * has to be right.
 */
export const settingsScreenHostFor = (
  id: string,
  hosts: SettingsScreenHostMap = settingsScreenHosts
): (() => Promise<void>) | undefined => hosts[id as SettingsScreenHostId];
